import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { brand } from "@/lib/site";
import LandingHeader from "@/components/LandingHeader";
import { formatBlogDate, setPageSeo } from "@/lib/seo";
import { ChevronRight } from "lucide-react";

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api.get(`/public/blog/${slug}`)
            .then((r) => setPost(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        if (!post) return;
        const title = post.seo_title || post.title;
        const description = post.seo_description || post.excerpt;
        setPageSeo({
            title: `${title} | ${brand.name} Blog`,
            description,
            image: post.cover_image_url || undefined,
            canonical: `${window.location.origin}/blog/${post.slug}`,
            type: "article",
        });
    }, [post]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif]">
                <LandingHeader />
                <div className="flex min-h-[50vh] items-center justify-center text-[#8A8178]">
                    Loading article…
                </div>
            </div>
        );
    }

    if (notFound || !post) {
        return (
            <div className="min-h-screen bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif]">
                <LandingHeader />
                <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6">
                    <p className="font-serif text-2xl text-[#4A0E17]">Article not found</p>
                    <Link to="/blog" className="text-sm font-semibold text-[#C84B31]">← Back to blog</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif]">
            <LandingHeader />

            <article className="mx-auto max-w-3xl px-5 py-10 sm:px-6 sm:py-14">
                <nav className="mb-6 flex items-center gap-1 text-xs font-medium text-[#8A8178]">
                    <Link to="/" className="hover:text-[#C84B31]">Home</Link>
                    <ChevronRight className="size-3" />
                    <Link to="/blog" className="hover:text-[#C84B31]">Blog</Link>
                </nav>

                {(post.tags || []).length > 0 && (
                    <div className="mb-4 flex flex-wrap gap-2">
                        {post.tags.map((tag) => (
                            <span
                                key={tag}
                                className="rounded-full bg-[#FDF3DC] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#9A7B10]"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                )}

                <h1 className="font-serif text-3xl font-medium leading-tight tracking-tight text-[#4A0E17] sm:text-4xl lg:text-[2.75rem]">
                    {post.title}
                </h1>

                <p className="mt-4 text-sm text-[#8A8178]">
                    {formatBlogDate(post.published_at || post.created_at)}
                    {post.author_name && <> · {post.author_name}</>}
                </p>

                {post.cover_image_url && (
                    <div className="mt-8 overflow-hidden rounded-2xl border border-[#E8E4DE] shadow-[0_12px_32px_rgba(45,41,38,0.08)]">
                        <img src={post.cover_image_url} alt="" className="aspect-[16/9] w-full object-cover" />
                    </div>
                )}

                <div
                    className="blog-article prose prose-stone mt-10 max-w-none text-[#3D2817] prose-headings:font-serif prose-headings:text-[#4A0E17] prose-a:text-[#C84B31] prose-strong:text-[#4A0E17] prose-blockquote:border-[#D4AF37] prose-blockquote:text-[#5C4033]"
                    dangerouslySetInnerHTML={{ __html: post.body_html }}
                />

                <div className="mt-14 rounded-2xl border border-[#E8E4DE] bg-white p-6 text-center">
                    <p className="font-serif text-xl text-[#4A0E17]">Try {brand.name} free</p>
                    <p className="mt-1 text-sm text-[#8A8178]">Bookings, GST billing &amp; reports in one platform.</p>
                    <Link
                        to="/register"
                        className="mt-4 inline-block rounded-md bg-[#4A0E17] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#3a0b12]"
                    >
                        Start free trial
                    </Link>
                </div>
            </article>

            <style>{`
                .blog-article h1, .blog-article h2, .blog-article h3 { margin-top: 1.5em; margin-bottom: 0.5em; }
                .blog-article p { margin-bottom: 1em; line-height: 1.75; }
                .blog-article ul, .blog-article ol { margin: 1em 0; padding-left: 1.5em; }
                .blog-article li { margin-bottom: 0.35em; }
                .blog-article img { border-radius: 12px; margin: 1.5em 0; max-width: 100%; }
                .blog-article a { text-decoration: underline; text-underline-offset: 2px; }
            `}</style>
        </div>
    );
}
